import { formatCurrency, formatDate } from '@/utils/formatters';
import type { DrawdownParameters } from './drawdownCreateTypes';
import type { DrawdownCalculationResult } from '@/services/drawdowns/drawdownCalculationService';
import type { Fund } from '@/types/fund';

interface DrawdownCreateReviewStepProps {
  parameters: DrawdownParameters;
  calculation: DrawdownCalculationResult;
  fund: Fund;
}

export function DrawdownCreateReviewStep({
  parameters,
  calculation,
  fund,
}: DrawdownCreateReviewStepProps) {
  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold">Review and Submit</h3>
        <p className="text-sm text-muted-foreground">
          Please review all details before creating the capital call
        </p>
      </div>

      <div>
        <h4 className="font-medium mb-3">Fund and Parameters</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground">Fund</div>
            <div className="font-medium">{fund.display_name || fund.name}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Type</div>
            <div className="font-medium">
              {parameters.callType === 'percentage'
                ? 'Percentage'
                : 'Fixed Amount'}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Value</div>
            <div className="font-medium">
              {parameters.callType === 'percentage'
                ? `${parameters.inputValue}%`
                : formatCurrency(parameters.inputValue, {
                    currency: parameters.currency,
                  })}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Issue Date</div>
            <div className="font-medium">
              {formatDate(parameters.issueDate)}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Due Date</div>
            <div className="font-medium">{formatDate(parameters.dueDate)}</div>
          </div>
          {calculation.is_first_drawdown &&
            calculation.fund_minimum_prepayment_pool && (
              <div>
                <div className="text-muted-foreground">
                  Prepayment (First Call)
                </div>
                <div className="font-medium">
                  {formatCurrency(calculation.fund_minimum_prepayment_pool, {
                    currency: parameters.currency,
                  })}{' '}
                  per investor
                </div>
              </div>
            )}
        </div>
        <div className="mt-4">
          <div className="text-muted-foreground text-sm">Title</div>
          <div className="font-medium">{parameters.purpose}</div>
        </div>
        {parameters.description && (
          <div className="mt-2">
            <div className="text-muted-foreground text-sm">Description</div>
            <div className="font-medium">{parameters.description}</div>
          </div>
        )}
      </div>

      <div>
        <h4 className="font-medium mb-3">Financial Summary</h4>
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-green-600 dark:text-green-400">
                Total Call Amount
              </div>
              <div className="font-medium text-lg">
                {formatCurrency(calculation.total_gross_amount, {
                  currency: parameters.currency,
                })}
              </div>
            </div>
            <div>
              <div className="text-green-600 dark:text-green-400">
                Prepayment Used
              </div>
              <div className="font-medium text-lg">
                {formatCurrency(calculation.total_prepayment_used, {
                  currency: parameters.currency,
                })}
              </div>
            </div>
            <div>
              <div className="text-green-600 dark:text-green-400">
                Net Amount Due
              </div>
              <div className="font-medium text-lg">
                {formatCurrency(calculation.total_net_amount, {
                  currency: parameters.currency,
                })}
              </div>
            </div>
            <div>
              <div className="text-green-600 dark:text-green-400">
                Total Investors
              </div>
              <div className="font-medium text-lg">
                {calculation.allocations.length}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
